/**
 * FeedbackButton — Chorus-style in-app feedback popover.
 * Allows users to report bugs (SIM-T) or request features (Taskei) with auto-context.
 */
import React, { useState, useRef, useEffect } from 'react';
import { FeedbackType, buildFeedbackUrl, validateDescription, FeedbackContext } from './feedback-utils';

interface FeedbackButtonProps {
  context: FeedbackContext;
  colors: {
    text: string;
    textMuted: string;
    border: string;
    sidebarBg: string;
  };
}

type FeedbackStep = 'closed' | 'select_type' | 'describe';

/**
 * Renders a 🐛 feedback button that opens a popover for bug reports or feature requests.
 * On submit, opens a pre-filled SIM-T or Taskei form in a new tab.
 */
export function FeedbackButton({ context, colors }: FeedbackButtonProps): React.ReactElement {
  const [step, setStep] = useState<FeedbackStep>('closed');
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (step === 'closed') return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        handleClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [step]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  function handleClose(): void {
    setStep('closed');
    setDescription('');
    setError(null);
  }

  function handleTypeSelect(type: FeedbackType): void {
    setFeedbackType(type);
    setStep('describe');
    setError(null);
  }

  function handleSubmit(): void {
    const validationError = validateDescription(description);
    if (validationError) {
      setError(validationError);
      return;
    }

    const url = buildFeedbackUrl(feedbackType, description.trim(), context);
    window.open(url, '_blank');

    const label = feedbackType === 'bug' ? 'bug report' : 'feature request';
    setToast(`Opening ${label} form...`);
    handleClose();
  }

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 12px',
    marginBottom: '8px',
    backgroundColor: '#7c4dff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  };

  const popoverStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    right: 0,
    marginBottom: '8px',
    backgroundColor: colors.sidebarBg,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1100,
  };

  const typeButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    marginBottom: '6px',
    backgroundColor: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontSize: '13px',
    color: colors.text,
  };

  return (
    <div style={{ position: 'relative' }} ref={popoverRef}>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#4caf50',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '6px',
          fontSize: '13px',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Popover */}
      {step !== 'closed' && (
        <div style={popoverStyle}>
          {step === 'select_type' && (
            <>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: colors.text }}>
                What would you like to do?
              </div>
              <button
                onClick={() => handleTypeSelect('bug')}
                style={typeButtonStyle}
              >
                🐛 Report a Bug
              </button>
              <button
                onClick={() => handleTypeSelect('feature_request')}
                style={typeButtonStyle}
              >
                💡 Request a Feature
              </button>
            </>
          )}

          {step === 'describe' && (
            <>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: colors.text }}>
                {feedbackType === 'bug' ? '🐛 Report a Bug' : '💡 Request a Feature'}
              </div>
              {feedbackType === 'feature_request' && (
                <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '8px', padding: '6px 8px', backgroundColor: `${colors.border}44`, borderRadius: '4px' }}>
                  Enter a short summary below (max 200 characters). You'll be able to fill in the full details in SIM after submitting.
                </div>
              )}
              <textarea
                value={description}
                onChange={(e) => { setDescription(e.target.value); setError(null); }}
                placeholder={feedbackType === 'bug' ? 'Describe the issue...' : 'Describe the feature you\'d like...'}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '8px',
                  border: `1px solid ${error ? '#f44336' : colors.border}`,
                  borderRadius: '4px',
                  fontSize: '12px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  backgroundColor: 'transparent',
                  color: colors.text,
                }}
                autoFocus
              />
              {error && (
                <div style={{ fontSize: '11px', color: '#f44336', marginTop: '4px' }}>
                  {error}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button
                  onClick={handleClose}
                  style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: '12px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  style={{ padding: '6px 12px', backgroundColor: '#7c4dff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                >
                  Submit
                </button>
              </div>
              <div style={{ fontSize: '10px', color: colors.textMuted, marginTop: '8px' }}>
                Opens in a new tab with your context pre-filled.
              </div>
            </>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setStep(step === 'closed' ? 'select_type' : 'closed')}
        style={buttonStyle}
        title="Report a bug or request a feature"
      >
        🐛 Feedback
      </button>
    </div>
  );
}
